import * as z from 'zod';
import {
  merchantCapabilitiesSchema,
  merchantResponseSchema,
  merchantSchema,
  type ListResult,
  type Merchant,
  type MerchantCapabilities,
  type PaginationParams,
} from '../model';
import {
  deleteOne,
  getList,
  getOne,
  patchOne,
  postOne,
  type RequestOptions,
} from './http';

// Merchant Service — the company that owns the branches. The panel reads this
// to resolve the merchant it is operating as; create/delete exist for
// completeness and are not exercised by the merchant panel itself.

/** Filters for `GET /admin/catalog-merchants`. */
export type ListMerchantsParams = PaginationParams & {
  include_hidden?: boolean;
};

/** `PATCH /admin/catalog-merchants/{id}` body. */
export interface UpdateMerchantInput {
  name?: string;
  status?: string;
}

export const merchantApi = {
  capabilities(options?: RequestOptions): Promise<MerchantCapabilities> {
    return getOne(
      'merchant/capabilities',
      merchantCapabilitiesSchema,
      undefined,
      options,
    );
  },

  /**
   * Centrifugo channels this merchant should subscribe to on the realtime
   * websocket (`GET /merchants/me/realtime`, `GetMyRealtimeChannels`).
   *
   * The list is a HINT, not a pass: the caller subscribes to every channel it
   * names, and Centrifugo re-checks the right to each one at subscribe time
   * against its owner (subscribe proxy). A new merchant channel is a backend
   * change to this endpoint — the frontend never assembles channel names.
   */
  async realtimeChannels(options?: RequestOptions): Promise<string[]> {
    const { list } = await getList(
      'merchants/me/realtime',
      z.string(),
      undefined,
      options,
    );
    return list;
  },

  list(
    params?: ListMerchantsParams,
    options?: RequestOptions,
  ): Promise<ListResult<Merchant>> {
    return getList('admin/catalog-merchants', merchantSchema, params, options);
  },

  async get(id: string, options?: RequestOptions): Promise<Merchant> {
    const { merchant } = await getOne(
      `admin/catalog-merchants/${id}`,
      merchantResponseSchema,
      undefined,
      options,
    );
    return merchant;
  },

  async create(name: string): Promise<Merchant> {
    const { merchant } = await postOne('merchants', { name }, merchantResponseSchema);
    return merchant;
  },

  async update(id: string, input: UpdateMerchantInput): Promise<Merchant> {
    const { merchant } = await patchOne(
      `admin/catalog-merchants/${id}`,
      input,
      merchantResponseSchema,
    );
    return merchant;
  },

  remove(id: string): Promise<void> {
    return deleteOne(`admin/catalog-merchants/${id}`);
  },
};
